import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, addDays, startOfDay } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, CalendarCheck, Loader2, Package } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/api";
import { FALLBACK_TREATMENTS, PACKAGES, WHATSAPP_LINK } from "@/config/site";

const STEPS = ["Trattamento", "Date e orari", "I tuoi dati"];

const SessionPicker = ({ index, value, onChange, tomorrow, maxDate }) => {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [closed, setClosed] = useState(false);

    useEffect(() => {
        if (!value.date) return;
        setLoading(true);
        api.get("/bookings/availability", { params: { date: format(value.date, "yyyy-MM-dd") } })
            .then((res) => {
                setSlots(res.data.slots);
                setClosed(Boolean(res.data.closed));
            })
            .catch(() => setSlots([]))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.date]);

    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-[0_4px_20px_rgba(67,46,30,0.05)]" data-testid={`session-picker-${index}`}>
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.22em] text-sage">
                Incontro {index + 1} di 4
                {value.date && value.time && (
                    <span className="ml-2 text-ink">
                        · {format(value.date, "EEE d MMM", { locale: it })} ore {value.time}
                    </span>
                )}
            </p>
            <Calendar
                mode="single"
                selected={value.date}
                onSelect={(d) => onChange({ date: d, time: "" })}
                locale={it}
                disabled={[{ before: tomorrow }, { after: maxDate }]}
                data-testid={`session-date-${index}`}
                className="rounded-xl"
            />
            {loading ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-fog">
                    <Loader2 className="h-4 w-4 animate-spin" /> Carico disponibilità…
                </div>
            ) : closed ? (
                <p className="mt-3 text-sm text-clay">Giorno di chiusura: scegli un altro giorno.</p>
            ) : value.date ? (
                <div className="mt-3 grid grid-cols-3 gap-2" data-testid={`session-slots-${index}`}>
                    {slots.map((s) => (
                        <button
                            key={s.time}
                            type="button"
                            data-testid={`session-slot-${index}-${s.time.replace(":", "")}`}
                            disabled={!s.available}
                            onClick={() => onChange({ ...value, time: s.time })}
                            className={`rounded-full border px-2 py-2 text-xs transition-all duration-300 ${
                                value.time === s.time
                                    ? "border-sage bg-sage text-cream"
                                    : s.available
                                      ? "border-border text-ink hover:border-sage hover:text-sage"
                                      : "cursor-not-allowed border-border/50 text-fog/50 line-through"
                            }`}
                        >
                            {s.time}
                        </button>
                    ))}
                </div>
            ) : (
                <p className="mt-3 text-sm text-fog">Scegli il giorno, poi l'orario.</p>
            )}
        </div>
    );
};

const BookingWizard = ({ initialTreatment = "" }) => {
    const [step, setStep] = useState(1);
    const [treatments, setTreatments] = useState(FALLBACK_TREATMENTS);
    const [packages, setPackages] = useState(PACKAGES);
    const [treatmentId, setTreatmentId] = useState(initialTreatment);
    const [date, setDate] = useState(undefined);
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [closed, setClosed] = useState(false);
    const [time, setTime] = useState("");
    const [sessions, setSessions] = useState([
        { date: undefined, time: "" },
        { date: undefined, time: "" },
        { date: undefined, time: "" },
        { date: undefined, time: "" },
    ]);
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);

    const tomorrow = useMemo(() => addDays(startOfDay(new Date()), 1), []);
    const maxDate = useMemo(() => addDays(startOfDay(new Date()), 60), []);
    const isPackage = treatmentId.startsWith("pkg-");
    const selectedItem = isPackage
        ? packages.find((p) => p.id === treatmentId)
        : treatments.find((t) => t.id === treatmentId);

    useEffect(() => {
        api.get("/treatments")
            .then((res) => {
                setTreatments(res.data.treatments);
                if (res.data.packages) setPackages(res.data.packages);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!date || isPackage) return;
        setTime("");
        setSlotsLoading(true);
        api.get("/bookings/availability", { params: { date: format(date, "yyyy-MM-dd") } })
            .then((res) => {
                setSlots(res.data.slots);
                setClosed(Boolean(res.data.closed));
            })
            .catch(() => setSlots([]))
            .finally(() => setSlotsLoading(false));
    }, [date, isPackage]);

    const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const setSession = (i) => (val) => setSessions((ss) => ss.map((s, j) => (j === i ? val : s)));

    const formValid =
        form.first_name.trim() &&
        form.last_name.trim() &&
        /^\S+@\S+\.\S+$/.test(form.email) &&
        form.phone.trim().length >= 6;

    const step2Valid = isPackage
        ? sessions.every((s) => s.date && s.time)
        : Boolean(date && time);

    const submit = async () => {
        if (!formValid || submitting) return;
        setSubmitting(true);
        try {
            const payload = isPackage
                ? {
                      treatment_id: treatmentId,
                      sessions: sessions.map((s) => ({ date: format(s.date, "yyyy-MM-dd"), time: s.time })),
                      ...form,
                  }
                : {
                      treatment_id: treatmentId,
                      date: format(date, "yyyy-MM-dd"),
                      time,
                      ...form,
                  };
            const res = await api.post("/bookings", payload);
            setConfirmation(res.data.booking);
            toast.success("Prenotazione inviata. Controlla la tua email per la conferma.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const cardClass = (active) =>
        `group relative overflow-hidden rounded-2xl border-2 text-left transition-all duration-300 ${
            active ? "border-sage shadow-[0_12px_40px_rgba(82,117,87,0.18)]" : "border-transparent hover:border-beige"
        }`;

    return (
        <div>
            {!confirmation && (
                <div className="mt-10 flex items-center gap-2" data-testid="booking-steps-indicator">
                    {STEPS.map((label, i) => (
                        <div key={label} className="flex flex-1 flex-col gap-2">
                            <div className={`h-1 rounded-full transition-colors duration-500 ${step > i ? "bg-sage" : "bg-border"}`} />
                            <span className={`text-[11px] uppercase tracking-widest ${step > i ? "text-sage" : "text-fog"}`}>
                                {i + 1}. {label}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* ===== STEP 1: trattamento o pacchetto ===== */}
            {step === 1 && !confirmation && (
                <Reveal className="mt-10">
                    <div className="grid gap-4 sm:grid-cols-2" data-testid="booking-step-treatment">
                        {treatments.map((t) => (
                            <button key={t.id} type="button" data-testid={`booking-treatment-${t.id}`} onClick={() => setTreatmentId(t.id)} className={cardClass(treatmentId === t.id)}>
                                <div className="h-36 overflow-hidden">
                                    <img src={t.image} alt={t.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                </div>
                                <div className="bg-card p-5">
                                    <p className="text-[11px] uppercase tracking-[0.22em] text-sage">{t.tagline}</p>
                                    <p className="mt-1 font-serif text-xl text-ink">{t.name}</p>
                                    <p className="mt-1 text-xs text-fog">{t.duration} · {t.price}</p>
                                </div>
                                {treatmentId === t.id && (
                                    <span className="absolute right-4 top-4 rounded-full bg-sage p-1.5 text-cream">
                                        <Check className="h-4 w-4" strokeWidth={2} />
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    <p className="mt-10 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-bark">
                        <Package className="h-4 w-4" strokeWidth={1.5} /> Pacchetti · 4 incontri con il 10% di sconto · validità 1 mese
                    </p>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="booking-step-packages">
                        {packages.map((p) => (
                            <button key={p.id} type="button" data-testid={`booking-package-${p.id}`} onClick={() => setTreatmentId(p.id)} className={cardClass(treatmentId === p.id)}>
                                <div className="flex items-center justify-between bg-sand p-5">
                                    <div>
                                        <p className="font-serif text-lg leading-snug text-ink">{p.name}</p>
                                        <p className="mt-1 text-xs text-fog">4 incontri · {p.duration}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-fog line-through">{p.original}</p>
                                        <p className="font-serif text-xl text-sage-dark">{p.price}</p>
                                    </div>
                                    {treatmentId === p.id && (
                                        <span className="absolute right-4 top-4 rounded-full bg-sage p-1.5 text-cream">
                                            <Check className="h-4 w-4" strokeWidth={2} />
                                        </span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="mt-8 flex justify-end">
                        <button
                            type="button"
                            data-testid="booking-next-step1"
                            disabled={!treatmentId}
                            onClick={() => setStep(2)}
                            className="inline-flex items-center gap-2 rounded-full bg-sage px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Continua <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </Reveal>
            )}

            {/* ===== STEP 2: data e ora (singolo) o 4 incontri (pacchetto) ===== */}
            {step === 2 && !confirmation && (
                <Reveal className="mt-10">
                    {isPackage ? (
                        <div className="grid gap-6 md:grid-cols-2" data-testid="booking-step-sessions">
                            {sessions.map((s, i) => (
                                <SessionPicker key={i} index={i} value={s} onChange={setSession(i)} tomorrow={tomorrow} maxDate={maxDate} />
                            ))}
                        </div>
                    ) : (
                        <div className="grid gap-8 md:grid-cols-2" data-testid="booking-step-datetime">
                            <div className="rounded-2xl border border-border bg-card p-5 shadow-[0_4px_20px_rgba(67,46,30,0.05)]">
                                <p className="mb-2 text-xs font-medium uppercase tracking-[0.22em] text-sage">Scegli il giorno</p>
                                <Calendar
                                    mode="single"
                                    selected={date}
                                    onSelect={setDate}
                                    locale={it}
                                    disabled={[{ before: tomorrow }, { after: maxDate }]}
                                    data-testid="booking-select-date"
                                    className="rounded-xl"
                                />
                                <p className="mt-2 text-xs italic text-fog">La prenotazione deve avere un preavviso di almeno 1 giorno.</p>
                            </div>
                            <div className="rounded-2xl border border-border bg-card p-5 shadow-[0_4px_20px_rgba(67,46,30,0.05)]">
                                <p className="mb-4 text-xs font-medium uppercase tracking-[0.22em] text-sage">
                                    {date ? `Orari · ${format(date, "EEEE d MMMM", { locale: it })}` : "Prima scegli una data"}
                                </p>
                                {slotsLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-fog" data-testid="booking-slots-loading">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Carico disponibilità…
                                    </div>
                                ) : closed ? (
                                    <p className="text-sm leading-relaxed text-clay" data-testid="booking-closed-message">
                                        In questo giorno lo studio è chiuso. Siamo aperti da martedì a
                                        venerdì e il sabato mattina: scegli un altro giorno sul calendario.
                                    </p>
                                ) : date ? (
                                    <div className="grid grid-cols-3 gap-3" data-testid="booking-slots-grid">
                                        {slots.map((s) => (
                                            <button
                                                key={s.time}
                                                type="button"
                                                data-testid={`booking-slot-${s.time.replace(":", "")}`}
                                                disabled={!s.available}
                                                onClick={() => setTime(s.time)}
                                                className={`rounded-full border px-3 py-2.5 text-sm transition-all duration-300 ${
                                                    time === s.time
                                                        ? "border-sage bg-sage text-cream"
                                                        : s.available
                                                          ? "border-border text-ink hover:border-sage hover:text-sage"
                                                          : "cursor-not-allowed border-border/50 text-fog/50 line-through"
                                                }`}
                                            >
                                                {s.time}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-fog">Seleziona un giorno sul calendario per vedere gli orari disponibili.</p>
                                )}
                                <p className="mt-4 text-xs italic text-fog">Gli orari sono indicativi e vengono confermati dallo studio.</p>
                            </div>
                        </div>
                    )}
                    <div className="mt-8 flex justify-between">
                        <button type="button" data-testid="booking-back-step2" onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-fog transition-colors hover:text-sage">
                            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Indietro
                        </button>
                        <button
                            type="button"
                            data-testid="booking-next-step2"
                            disabled={!step2Valid}
                            onClick={() => setStep(3)}
                            className="inline-flex items-center gap-2 rounded-full bg-sage px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Continua <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </Reveal>
            )}

            {/* ===== STEP 3: dati + riepilogo ===== */}
            {step === 3 && !confirmation && (
                <Reveal className="mt-10">
                    <div className="grid gap-8 md:grid-cols-5" data-testid="booking-step-details">
                        <div className="rounded-2xl bg-sand p-6 md:col-span-2">
                            <p className="text-xs font-medium uppercase tracking-[0.22em] text-sage">Riepilogo</p>
                            <p className="mt-4 font-serif text-2xl text-ink" data-testid="booking-summary-treatment">
                                {isPackage ? `Pacchetto 4 incontri · ${selectedItem?.name}` : selectedItem?.name}
                            </p>
                            {isPackage ? (
                                <ul className="mt-3 space-y-1.5 text-sm text-clay" data-testid="booking-summary-sessions">
                                    {sessions.map((s, i) => (
                                        <li key={i}>
                                            {i + 1}. {s.date && format(s.date, "EEE d MMM yyyy", { locale: it })} · ore {s.time}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="mt-2 text-sm text-clay" data-testid="booking-summary-datetime">
                                    {date && format(date, "EEEE d MMMM yyyy", { locale: it })} · ore {time}
                                </p>
                            )}
                            <p className="mt-2 text-xs text-fog">
                                {isPackage ? selectedItem?.price : `${selectedItem?.duration} · ${selectedItem?.price}`}
                            </p>
                            <p className="mt-5 text-xs leading-relaxed text-fog">
                                Inviando il modulo accetti di essere ricontattato per la gestione
                                dell’appuntamento. Nessun pagamento è richiesto ora.
                            </p>
                        </div>
                        <div className="md:col-span-3">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="nome" className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-clay">Nome</label>
                                    <Input id="nome" data-testid="booking-input-nome" value={form.first_name} onChange={setField("first_name")} placeholder="Maria" className="rounded-xl border-border bg-card" required />
                                </div>
                                <div>
                                    <label htmlFor="cognome" className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-clay">Cognome</label>
                                    <Input id="cognome" data-testid="booking-input-cognome" value={form.last_name} onChange={setField("last_name")} placeholder="Rossi" className="rounded-xl border-border bg-card" required />
                                </div>
                                <div>
                                    <label htmlFor="email" className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-clay">Email</label>
                                    <Input id="email" type="email" data-testid="booking-input-email" value={form.email} onChange={setField("email")} placeholder="maria@email.it" className="rounded-xl border-border bg-card" required />
                                </div>
                                <div>
                                    <label htmlFor="telefono" className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-clay">Cellulare</label>
                                    <Input id="telefono" type="tel" data-testid="booking-input-telefono" value={form.phone} onChange={setField("phone")} placeholder="+39 333 000 0000" className="rounded-xl border-border bg-card" required />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="note" className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-clay">Note (facoltativo)</label>
                                    <Textarea id="note" data-testid="booking-input-note" value={form.notes} onChange={setField("notes")} placeholder="Qualcosa che dovrei sapere prima del trattamento?" rows={3} className="rounded-xl border-border bg-card" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 flex justify-between">
                        <button type="button" data-testid="booking-back-step3" onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-fog transition-colors hover:text-sage">
                            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Indietro
                        </button>
                        <button
                            type="button"
                            data-testid="booking-submit-button"
                            disabled={!formValid || submitting}
                            onClick={submit}
                            className="inline-flex items-center gap-2 rounded-full bg-sage px-10 py-4 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" strokeWidth={1.5} />}
                            {submitting ? "Invio in corso…" : "Conferma prenotazione"}
                        </button>
                    </div>
                    <p className="mt-5 text-center text-xs italic text-fog" data-testid="booking-notice">
                        La prenotazione deve avere un preavviso di almeno 1 giorno: lo studio ti risponderà
                        con una email di conferma.
                    </p>
                </Reveal>
            )}

            {/* ===== SUCCESS ===== */}
            {confirmation && (
                <Reveal className="mt-10">
                    <div className="rounded-3xl border border-sage/30 bg-sage-light/60 p-10 text-center md:p-14" data-testid="booking-success">
                        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage text-cream">
                            <Check className="h-7 w-7" strokeWidth={2} />
                        </span>
                        <h2 className="mt-6 font-serif text-3xl text-ink">Richiesta inviata, {confirmation.first_name}</h2>
                        {confirmation.kind === "package" ? (
                            <div className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-clay">
                                <p>
                                    Abbiamo registrato il tuo <strong>{confirmation.treatment_name}</strong> ({confirmation.price}):
                                </p>
                                <ul className="mt-3 space-y-1" data-testid="booking-success-sessions">
                                    {(confirmation.sessions || []).map((s, i) => (
                                        <li key={i}>
                                            {i + 1}. {format(new Date(`${s.date}T00:00:00`), "EEEE d MMMM", { locale: it })} · ore {s.time}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-3">Ti abbiamo inviato una email di riepilogo; lo studio confermerà personalmente al più presto.</p>
                            </div>
                        ) : (
                            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-clay">
                                Abbiamo registrato il tuo appuntamento per{" "}
                                <strong>{confirmation.treatment_name}</strong> —{" "}
                                {format(new Date(`${confirmation.date}T00:00:00`), "EEEE d MMMM yyyy", { locale: it })} alle{" "}
                                {confirmation.time}. Ti abbiamo inviato una email di riepilogo; lo studio
                                confermerà personalmente al più presto.
                            </p>
                        )}
                        <p className="mt-6 text-xs uppercase tracking-[0.25em] text-fog">Codice prenotazione</p>
                        <p className="mt-2 font-serif text-3xl tracking-[0.2em] text-sage-dark" data-testid="booking-confirmation-code">
                            {confirmation.code}
                        </p>
                        <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                            <a
                                href={WHATSAPP_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="booking-success-whatsapp"
                                className="rounded-full border border-sage px-7 py-3 text-sm font-semibold uppercase tracking-widest text-sage transition-colors duration-300 hover:bg-sage hover:text-cream"
                            >
                                Scrivici su WhatsApp
                            </a>
                            <Link
                                to="/"
                                data-testid="booking-success-home"
                                className="rounded-full bg-sage px-7 py-3 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark"
                            >
                                Torna alla home
                            </Link>
                        </div>
                    </div>
                </Reveal>
            )}
        </div>
    );
};

export default BookingWizard;
